import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import AppEmptyState from "../../components/app/AppEmptyState";
import {
  downloadBookPdf,
  getBookMeta,
  listProfiles,
  type BookMeta,
  type ProfileSummary,
} from "../../lib/api";
import { FullPageLoading, InlineSpinner } from "../../components/Loading";
import { STUB_BOOK } from "../../lib/stubs";
import { BETA_MODE } from "../../lib/beta";
import {
  chapterNumber,
  chapterTitleWithoutNumber,
  toRoman,
} from "../../lib/format";

// PDF herunterladen · Endfassung. Companion formats (Hörbuch, Podcast) are
// locked rows during Beta. STUB_BOOK still drives the version-history block
// until we have a real revisions feed (TODO(Phase C)).

export default function AppBook() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [meta, setMeta] = useState<BookMeta | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0];
        if (!p) return;
        setProfile(p);
        const m = await getBookMeta(p.phone);
        setMeta(m);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <FullPageLoading />;

  if (!profile) {
    return (
      <AppShell
        eyebrow="Mein Buch · PDF"
        title={
          <>
            Bereit zum <em>Herunterladen</em>
          </>
        }
      >
        <AppEmptyState
          icon="book"
          title="Noch kein Erzähler-Profil verknüpft."
          body="Das Buch entsteht aus den Gesprächen Ihres Erzählers — sobald ein Profil verknüpft ist."
        />
      </AppShell>
    );
  }

  const firstName =
    meta?.interviewee_first_name ||
    profile?.interviewee_name?.split(" ")[0] ||
    "Vitanara";
  const lastName =
    meta?.interviewee_last_name ||
    profile?.interviewee_name?.split(" ").slice(1).join(" ") ||
    "";

  const facts = {
    pages: meta?.estimated_pages || 0,
    sizeKb: meta?.estimated_kb || 0,
    chapters: meta?.chapter_count_with_content || 0,
  };

  async function onDownload() {
    if (!profile || !meta?.has_book) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadBookPdf(profile.phone);
    } catch (e: unknown) {
      setDownloadError(
        e instanceof Error ? e.message : "Download fehlgeschlagen.",
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <AppShell
      eyebrow="Mein Buch · PDF"
      title={
        <>
          Bereit zum <em>Herunterladen</em>
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        {/* Big preview card with cover + facts + Endfassung stamp */}
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 16,
            padding: "20px 18px 18px",
            marginBottom: 18,
            boxShadow: "0 14px 28px -12px rgba(43,26,15,0.18)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Endfassung sage stamp, rotated */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 16,
              right: -28,
              transform: "rotate(18deg)",
              border: "2px solid var(--sage)",
              color: "var(--sage)",
              fontFamily: "var(--f-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              padding: "4px 36px",
              borderRadius: 4,
              opacity: 0.85,
              background: "var(--paper-50)",
            }}
          >
            Endfassung
          </div>

          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            {/* Book spine — oxblood linen with double inner border */}
            <BookSpine first={firstName} last={lastName} />

            <div style={{ flex: 1 }}>
              <div className="t-caps" style={{ marginBottom: 4 }}>
                Datei
              </div>
              <div
                style={{
                  fontFamily: "var(--f-display)",
                  fontWeight: 500,
                  fontSize: 17,
                  color: "var(--ink)",
                  lineHeight: 1.2,
                }}
              >
                {firstName}-{lastName}_<br />
                Band-I.pdf
              </div>
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  fontFamily: "var(--f-body)",
                  fontSize: 13,
                  color: "var(--ink-soft)",
                }}
              >
                <FactRow
                  k="Seiten"
                  v={facts.pages > 0 ? `~${facts.pages}` : "—"}
                />
                <FactRow
                  k="Kapitel"
                  v={facts.chapters > 0 ? `${facts.chapters} mit Inhalt` : "noch leer"}
                />
                <FactRow
                  k="Größe"
                  v={
                    facts.sizeKb > 0
                      ? facts.sizeKb >= 1024
                        ? `~${(facts.sizeKb / 1024).toFixed(1)} MB`
                        : `~${facts.sizeKb} KB`
                      : "—"
                  }
                />
                <FactRow k="Format" v="A5 · PDF" />
              </div>
            </div>
          </div>

          {/* Facts row */}
          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px dashed rgba(43,26,15,0.14)",
              display: "flex",
              gap: 14,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {facts.pages > 0 ? <span>~{facts.pages} S.</span> : <span>Buch im Aufbau</span>}
            <span>·</span>
            <span>{facts.chapters} / {meta?.chapter_count_total ?? 13} Kapitel</span>
            <span>·</span>
            <span>A5</span>
          </div>
        </div>

        {/* Primary download — wired to Google-Doc-to-PDF export */}
        <button
          type="button"
          className="btn-primary"
          onClick={onDownload}
          disabled={!meta?.has_book || downloading}
          style={{
            width: "100%",
            boxSizing: "border-box",
            opacity: !meta?.has_book ? 0.55 : 1,
            cursor: !meta?.has_book ? "not-allowed" : downloading ? "wait" : "pointer",
          }}
        >
          {downloading ? (
            <InlineSpinner />
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          )}
          {downloading ? "Wird vorbereitet …" : "PDF herunterladen"}
        </button>
        {meta && !meta.has_book && (
          <div
            style={{
              textAlign: "center",
              marginTop: 10,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
            }}
          >
            Das Buch entsteht noch — sobald genug Inhalt da ist, wird es hier
            zum Download bereitstehen.
          </div>
        )}
        {meta?.has_book && (
          <div
            style={{
              textAlign: "center",
              marginTop: 10,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 12.5,
              color: "var(--ink-faint)",
            }}
          >
            Beliebig oft herunterladbar · gehört für immer der Familie
          </div>
        )}
        {downloadError && (
          <div
            role="alert"
            style={{
              marginTop: 8,
              padding: "8px 12px",
              background: "rgba(184,52,40,0.08)",
              border: "1px solid rgba(184,52,40,0.30)",
              borderRadius: 8,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--error)",
              textAlign: "center",
            }}
          >
            {downloadError}
          </div>
        )}

        {/* Read as plain text */}
        <Link
          to="/app/book/text"
          className="btn-secondary"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 18,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M4 6h16M4 12h16M4 18h10" />
          </svg>
          Als Text lesen
        </Link>

        {/* Per E-Mail teilen — routes to /app/book/share */}
        <Link
          to="/app/book/share"
          className="btn-secondary"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 10,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
          </svg>
          Per E-Mail teilen
        </Link>

        {/* Chapters with content — Roman numeral · title · saturation %. */}
        <ChaptersWithContent meta={meta} />

        {/* Companion formats — locked during Beta */}
        <div style={{ marginTop: 22 }}>
          <div className="t-caps" style={{ marginBottom: 10 }}>
            Auch verfügbar
          </div>
          <CompanionRow
            icon="audio"
            name="Hörbuch · in ihrer Stimme"
            sub="MP3 · in Premium enthalten"
            locked={BETA_MODE}
          />
          <CompanionRow
            icon="podcast"
            name="Familien-Podcast"
            sub="Privater RSS-Feed · 12 Episoden"
            locked={BETA_MODE}
          />
        </div>

        {/* Version history */}
        <div style={{ marginTop: 22 }}>
          <div className="t-caps" style={{ marginBottom: 10 }}>
            Frühere Fassungen
          </div>
          <div
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 12,
              padding: "4px 16px",
            }}
          >
            {STUB_BOOK.versions.map((h, i) => (
              <div
                key={h.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 0",
                  borderBottom:
                    i < STUB_BOOK.versions.length - 1
                      ? "1px solid rgba(43,26,15,0.06)"
                      : "none",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: "var(--f-display)",
                      fontWeight: 500,
                      fontSize: 14,
                      color: h.current ? "var(--oxblood)" : "var(--ink)",
                    }}
                  >
                    {h.label}{" "}
                    <span
                      style={{
                        fontFamily: "var(--f-body)",
                        fontStyle: "italic",
                        fontSize: 12,
                        color: "var(--ink-faint)",
                        fontWeight: 400,
                      }}
                    >
                      · {h.date}
                    </span>
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--f-body)",
                      fontStyle: "italic",
                      fontSize: 12,
                      color: "var(--ink-faint)",
                    }}
                  >
                    {h.note}
                  </div>
                </div>
                {h.current && (
                  <div
                    style={{
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: "var(--oxblood)",
                      color: "var(--paper-50)",
                      fontFamily: "var(--f-ui)",
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    aktuell
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function BookSpine({ first, last }: { first: string; last: string }) {
  return (
    <div
      style={{
        width: 110,
        height: 154,
        background: "var(--oxblood)",
        backgroundImage: `
          repeating-linear-gradient(0deg, rgba(0,0,0,0.10) 0 1px, transparent 1px 3px),
          repeating-linear-gradient(90deg, rgba(0,0,0,0.10) 0 1px, transparent 1px 3px),
          radial-gradient(ellipse at 30% 20%, rgba(255,220,170,0.12), transparent 60%)`,
        borderRadius: 3,
        boxShadow:
          "0 14px 28px -10px rgba(43,26,15,0.40), inset 0 0 0 1px rgba(0,0,0,0.20)",
        flexShrink: 0,
        padding: "18px 10px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        color: "var(--paper-50)",
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 6,
          border: "0.5px solid rgba(244,236,216,0.35)",
          borderRadius: 1,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          fontFamily: "var(--f-ui)",
          fontSize: 6.5,
          letterSpacing: "0.32em",
          color: "var(--ochre)",
          textAlign: "center",
          marginTop: 2,
        }}
      >
        VITANARA
      </div>
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            fontFamily: "var(--f-script)",
            fontSize: 11,
            color: "var(--ochre)",
          }}
        >
          Das Leben von
        </div>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontSize: 22,
            lineHeight: 1,
            marginTop: 2,
          }}
        >
          {first}
        </div>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontStyle: "italic",
            fontSize: 11,
            color: "rgba(244,236,216,0.7)",
            marginTop: 2,
          }}
        >
          {last}
        </div>
      </div>
      <div
        style={{
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontSize: 6.5,
          color: "rgba(244,236,216,0.55)",
          textAlign: "center",
        }}
      >
        Band I
      </div>
    </div>
  );
}

function FactRow({ k, v }: { k: string; v: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
      }}
    >
      <span
        style={{
          fontFamily: "var(--f-ui)",
          fontSize: 10.5,
          color: "var(--ink-faint)",
          textTransform: "uppercase",
          letterSpacing: "0.10em",
          fontWeight: 600,
        }}
      >
        {k}
      </span>
      <span
        style={{
          fontFamily: "var(--f-display)",
          fontSize: 13,
          color: "var(--ink)",
        }}
      >
        {v}
      </span>
    </div>
  );
}

function CompanionRow({
  icon,
  name,
  sub,
  locked,
}: {
  icon: "audio" | "podcast";
  name: string;
  sub: string;
  locked: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 16px",
        background: locked ? "rgba(43,26,15,0.03)" : "var(--paper-50)",
        border: locked
          ? "1px dashed rgba(43,26,15,0.20)"
          : "1px solid rgba(43,26,15,0.10)",
        borderRadius: 12,
        marginBottom: 8,
        opacity: locked ? 0.85 : 1,
      }}
      aria-disabled={locked ? "true" : undefined}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 8,
          background: locked ? "var(--paper-200)" : "rgba(168,122,58,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: locked ? "var(--ink-faint)" : "var(--oxblood)",
        }}
      >
        {icon === "audio" ? (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
        ) : (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12a4 4 0 018 0M5 12a7 7 0 0114 0" />
          </svg>
        )}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 500,
            fontSize: 14.5,
            color: locked ? "var(--ink-soft)" : "var(--ink)",
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12.5,
            color: "var(--ink-faint)",
          }}
        >
          {sub}
        </div>
      </div>
      {locked ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "var(--ink-faint)",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden
          >
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" />
          </svg>
          <span
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--ochre)",
            }}
          >
            Bald
          </span>
        </div>
      ) : (
        <button
          type="button"
          style={{
            padding: "8px 14px",
            borderRadius: 999,
            background: "transparent",
            border: "1px solid rgba(43,26,15,0.20)",
            fontFamily: "var(--f-ui)",
            fontSize: 11.5,
            fontWeight: 600,
            color: "var(--ink)",
            cursor: "pointer",
          }}
        >
          Öffnen
        </button>
      )}
    </div>
  );
}

function ChaptersWithContent({ meta }: { meta: BookMeta | null }) {
  const all = meta?.chapters || [];
  // Sort by chapter number derived from the title; we want a stable
  // arabic→roman index that matches /app/chapter/:n.
  const sorted = [...all].sort(
    (a, b) => chapterNumber(a.title) - chapterNumber(b.title),
  );
  // Index in the sorted array drives the /app/chapter/:n route, regardless
  // of whether the chapter has content yet. We list ONLY chapters with
  // content, but link them with their original sorted-array index.
  const enriched = sorted
    .map((c, idx) => ({ c, idx: idx + 1 }))
    .filter(({ c }) => (c.word_count || 0) > 0);

  return (
    <div style={{ marginTop: 26 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 10,
        }}
      >
        <div className="t-caps">Kapitel im Buch</div>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-faint)",
          }}
        >
          {enriched.length} mit Inhalt
        </div>
      </div>

      {enriched.length === 0 ? (
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 12,
            padding: "16px 18px",
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 14,
            color: "var(--ink-soft)",
          }}
        >
          Noch keine Kapitel mit Inhalt. Sobald die ersten Gespräche
          ausgewertet sind, erscheinen sie hier.
        </div>
      ) : (
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {enriched.map(({ c, idx }, i) => (
            <ChapterLine
              key={c.title}
              n={idx}
              title={c.title}
              percent={c.saturation_score}
              isLast={i === enriched.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChapterLine({
  n,
  title,
  percent,
  isLast,
}: {
  n: number;
  title: string;
  percent: number;
  isLast: boolean;
}) {
  const cleanTitle = chapterTitleWithoutNumber(title) || title;
  const safePercent = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const color =
    safePercent >= 80
      ? "var(--sage)"
      : safePercent >= 30
        ? "var(--ochre)"
        : "var(--ink-faint)";
  return (
    <Link
      to={`/app/chapter/${n}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        textDecoration: "none",
        color: "var(--ink)",
        borderBottom: isLast ? "none" : "1px solid rgba(43,26,15,0.06)",
      }}
    >
      <div
        style={{
          width: 36,
          flexShrink: 0,
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontSize: 18,
          color: "var(--oxblood)",
          textAlign: "center",
        }}
      >
        {toRoman(n)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 500,
            fontSize: 15,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {cleanTitle}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 4,
          }}
        >
          <div
            aria-hidden
            style={{
              flex: 1,
              height: 4,
              background: "rgba(43,26,15,0.08)",
              borderRadius: 999,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                width: `${safePercent}%`,
                background: color,
                borderRadius: 999,
                transition: "width 200ms ease-out",
              }}
            />
          </div>
          <span
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 11,
              color: "var(--ink-faint)",
              minWidth: 36,
              textAlign: "right",
            }}
          >
            {safePercent} %
          </span>
        </div>
      </div>
      <span
        aria-hidden
        style={{
          color: "var(--ink-faint)",
          fontFamily: "var(--f-ui)",
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        ›
      </span>
    </Link>
  );
}
